import { useTranslator, useUrlLocalizer } from 'tradera-lang/translate';
import Flag from 'tradera-components/flag/flag';
import React from 'react';
import { MutualFeedbackRemoval } from 'tradera-apps/tradera/components/profile/mutual-feedback-removal';
import { ReplyButton } from 'tradera-apps/tradera/components/profile/reply-button';
import { useDispatch, useSelector } from 'react-redux';
import { selectShippingRegionCountryCodeIso2 } from 'tradera-state/shipping-region/selectors';
import {
    selectTranslatedReview,
    selectHasTranslatedReview
} from 'tradera-state/views/profile/selectors';
import TranslateIcon from '@tradera/blueprint/theme/icons2/translate.svg?jsx';
import { loadAndShowTranslatedReviews } from 'tradera-state/views/profile/actions';

export const FeedbackItem = ({ feedbackItem }) => {
    const dispatch = useDispatch();
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const translatedReview = useSelector(
        selectTranslatedReview(feedbackItem.gradeId)
    );
    const hasTranslatedReview = useSelector(
        selectHasTranslatedReview(feedbackItem.gradeId)
    );

    const countryCodeSameAsFeedbackItemCountryCode =
        useSelector(selectShippingRegionCountryCodeIso2).toLowerCase() ===
        feedbackItem?.feedbackMemberCountryCodeIso2?.toLowerCase();

    const hasTranslateableReview =
        !hasTranslatedReview && !countryCodeSameAsFeedbackItemCountryCode;

    const feedBackItemClicked = e => {
        e.preventDefault();
        dispatch(loadAndShowTranslatedReviews([feedbackItem.gradeId]));
    };

    return (
        <li>
            <article className="feedback-item media">
                <div className="media-object feedback-rating">
                    <span
                        className={`feedback-item-rating feedback-item-rating-${feedbackItem.rating.toLowerCase()}`}
                    />
                </div>
                <div className="media-body">
                    <time
                        className="feedback-item-date"
                        dateTime={feedbackItem.createdDateTime}
                        title={feedbackItem.createdString}>
                        {feedbackItem.createdAgo}
                    </time>
                    <header className="feedback-item-header-react feedback-item-child-margins">
                        <strong>
                            <a
                                data-nav
                                className="link"
                                href={toLocalizedUrl(
                                    `/profile/feedback/${feedbackItem.feedbackMemberId}/${feedbackItem.slugifiedFeedbackMemberAlias}`
                                )}>
                                {feedbackItem.feedbackMemberAlias}
                            </a>
                        </strong>
                        <span className="feedback-item-role">
                            ({feedbackItem.feedBackMemberRoleDescription})
                        </span>
                        <span className="feedback-item-international feedback-item-child-margins">
                            {!countryCodeSameAsFeedbackItemCountryCode && (
                                <Flag
                                    className="feedback-item-flag"
                                    countryCodeIso2={
                                        feedbackItem.feedbackMemberCountryCodeIso2
                                    }
                                />
                            )}
                            {hasTranslatedReview && (
                                <strong>
                                    {t(
                                        'profileRating_googleTranslatedDisclaimer'
                                    )}
                                </strong>
                            )}
                            {feedbackItem.hasText && hasTranslateableReview && (
                                <button
                                    className="unbutton btn-link mx-1"
                                    onClick={feedBackItemClicked}>
                                    {t('profileRating_translate')}
                                </button>
                            )}
                        </span>
                    </header>
                    {hasTranslatedReview && (
                        <>
                            <div className="bg-info d-inline-block p-3 mt-3">
                                <TranslateIcon className="icon mr-2" />
                                <span>{translatedReview.text}</span>
                                {translatedReview.reply && (
                                    <>
                                        <strong className="feedback-item-reply-title">
                                            {t('profileRating_comment')}:
                                        </strong>
                                        <p className="m-0">
                                            {translatedReview.reply}
                                        </p>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {feedbackItem.hasText && (
                        <p className="feedback-item-comment feedback-item-meta">
                            {feedbackItem.text}
                        </p>
                    )}
                    {feedbackItem.hasReply && (
                        <>
                            <h5 className="feedback-item-reply-title feedback-item-meta mb-0">
                                {t('profileRating_comment')}:
                            </h5>
                            <p className="feedback-item-reply-text feedback-item-meta">
                                {feedbackItem.reply}
                            </p>
                        </>
                    )}
                    <p className="feedback-item-description feedback-item-meta mb-0">
                        {feedbackItem.hasPassed120Days ? (
                            <a
                                href={feedbackItem.canonicalUrl}
                                className="link"
                                data-nav>
                                {`${feedbackItem.itemId} - ${feedbackItem.itemDescription}`}
                            </a>
                        ) : (
                            feedbackItem.itemId
                        )}
                    </p>
                    <div className="feedback-item-action-buttons float-right">
                        {feedbackItem.mutualFeedbackRemovalItem && (
                            <MutualFeedbackRemoval
                                mutualFeedbackRemovalItem={
                                    feedbackItem.mutualFeedbackRemovalItem
                                }
                            />
                        )}

                        {feedbackItem.isReplyAllowed && (
                            <ReplyButton feedbackItem={feedbackItem} />
                        )}
                    </div>
                </div>
            </article>
        </li>
    );
};
